/**
 * HeroCarousel.tsx
 * Reemplaza la imagen estática del hero (columna derecha desktop).
 * - Carga config en tiempo real desde Firestore
 * - Ken Burns por slide (zoomIn / zoomOut / none)
 * - Transiciones: fade | slideLeft | slideUp
 * - Fallback: si no hay config/slides, muestra la imagen Unsplash original
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { db } from '../../config/firebase';
import { doc, onSnapshot } from 'firebase/firestore';
import { HeroCarouselConfig } from '../../types';

// ── Estilos inline (se inyectan UNA vez) ───────────────
const CSS = `
@keyframes hc-zoomIn  { from { transform: scale(1);    } to { transform: scale(1.14); } }
@keyframes hc-zoomOut { from { transform: scale(1.14); } to { transform: scale(1);    } }

.hc-img.hc-zoomIn  { animation: hc-zoomIn  var(--hc-dur) ease forwards; }
.hc-img.hc-zoomOut { animation: hc-zoomOut var(--hc-dur) ease forwards; }

/* FADE */
.hc-wrap[data-tr="fade"]      .hc-slide       { opacity: 0; transition: opacity 0.8s ease; }
.hc-wrap[data-tr="fade"]      .hc-slide.enter { opacity: 1; }

/* SLIDE LEFT */
.hc-wrap[data-tr="slideLeft"] .hc-slide        { transform: translateX(100%); transition: transform 0.75s cubic-bezier(.77,0,.18,1); }
.hc-wrap[data-tr="slideLeft"] .hc-slide.enter  { transform: translateX(0); }
.hc-wrap[data-tr="slideLeft"] .hc-slide.exit   { transform: translateX(-100%); }

/* SLIDE UP */
.hc-wrap[data-tr="slideUp"]   .hc-slide        { transform: translateY(100%); transition: transform 0.75s cubic-bezier(.77,0,.18,1); }
.hc-wrap[data-tr="slideUp"]   .hc-slide.enter  { transform: translateY(0); }
.hc-wrap[data-tr="slideUp"]   .hc-slide.exit   { transform: translateY(-100%); }
`;

let injected = false;
function injectCSS() {
    if (injected || typeof document === 'undefined') return;
    const el = document.createElement('style');
    el.textContent = CSS;
    document.head.appendChild(el);
    injected = true;
}

// ── Imagen fallback (original del proyecto) ─────────────
const FALLBACK_IMG   = 'https://images.unsplash.com/photo-1589829085413-56de8ae18c73?q=80&w=2112&auto=format&fit=crop';
const FALLBACK_LABEL = 'Calidad Offset Digital';
const FALLBACK_DESC  = 'Colores vibrantes y acabados perfectos';

// ── Componente ──────────────────────────────────────────
interface Props {
    /** Clases extra para el wrapper (ej. rounded-[2rem] overflow-hidden) */
    className?: string;
}

const HeroCarousel: React.FC<Props> = ({ className = '' }) => {
    const [cfg, setCfg]         = useState<HeroCarouselConfig | null>(null);
    const [active, setActive]   = useState(0);
    const [exiting, setExiting] = useState<number | null>(null);
    const [imgKey, setImgKey]   = useState(0);
    const timer                 = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => { injectCSS(); }, []);

    // Suscripción en tiempo real
    useEffect(() => {
        const unsub = onSnapshot(
            doc(db, 'settings', 'heroCarousel'),
            snap => setCfg(snap.exists() ? (snap.data() as HeroCarouselConfig) : null),
        );
        return unsub;
    }, []);

    const slides  = cfg?.slides ?? [];
    const interval = (cfg?.intervalSeconds ?? 6) * 1000;
    const tr      = cfg?.transition ?? 'fade';

    const advance = useCallback(() => {
        if (slides.length < 2) return;
        setExiting(prev => prev ?? active);
        setActive(prev => (prev + 1) % slides.length);
        setImgKey(k => k + 1);
        setTimeout(() => setExiting(null), 850);
    }, [active, slides.length]);

    useEffect(() => {
        if (!cfg?.enabled || slides.length < 2) return;
        if (timer.current) clearInterval(timer.current);
        timer.current = setInterval(advance, interval);
        return () => { if (timer.current) clearInterval(timer.current); };
    }, [cfg, slides.length, interval, advance]);

    const goTo = (i: number) => {
        if (i === active) return;
        if (timer.current) clearInterval(timer.current);
        setExiting(active);
        setActive(i);
        setImgKey(k => k + 1);
        setTimeout(() => setExiting(null), 850);
        timer.current = setInterval(advance, interval);
    };

    // ── LOADING (config aún no cargada) ─────────────────
    if (cfg === null) {
        return (
            <div className={`transform rotate-2 hover:rotate-0 transition-transform duration-700 ${className}`}>
                <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl" style={{ height: '550px', clipPath: 'inset(0 round 2rem)' }}>
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 animate-pulse" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-40" />
                    <div className="absolute bottom-6 left-6 right-6 p-5 bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-600/50 rounded-full flex items-center justify-center shadow-lg">
                                <i className="fas fa-print text-white/50 text-xl"></i>
                            </div>
                            <div className="space-y-2">
                                <div className="h-4 bg-white/10 rounded w-40" />
                                <div className="h-3 bg-white/5 rounded w-56" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── FALLBACK (carrusel desactivado o sin slides) ────
    if (!cfg?.enabled || slides.length === 0) {
        return (
            <div className={`transform rotate-2 hover:rotate-0 transition-transform duration-700 ${className}`}>
                <div className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl" style={{ clipPath: 'inset(0 round 2rem)' }}>
                    <img
                        src={FALLBACK_IMG}
                        alt="Muestras de impresión de alta calidad"
                        className="w-full h-[550px] object-cover"
                        fetchPriority="high"
                        width={800}
                        height={550}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-40" />
                    <div className="absolute bottom-6 left-6 right-6 p-6 bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg">
                                <i className="fas fa-print text-white text-xl"></i>
                            </div>
                            <div>
                                <p className="text-white font-bold">{FALLBACK_LABEL}</p>
                                <p className="text-slate-300 text-sm">{FALLBACK_DESC}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // ── CARRUSEL ACTIVO ────────────────────────────────
    const durCss = `${(cfg.intervalSeconds ?? 6) + 1}s`;
    const currentSlide = slides[active];

    return (
        <div className={`transform rotate-2 hover:rotate-0 transition-transform duration-700 ${className}`}>
        <div
            className="relative rounded-[2rem] overflow-hidden border border-white/10 shadow-2xl"
            style={{ height: '550px', clipPath: 'inset(0 round 2rem)' }}
        >
            {/* Wrapper con data-tr para CSS transitions */}
            <div className="hc-wrap absolute inset-0" data-tr={tr}>

                {slides.map((slide, idx) => {
                    const isActive  = idx === active;
                    const isExiting = idx === exiting;
                    const cls = isActive ? 'enter' : isExiting ? 'exit' : '';

                    return (
                        <div
                            key={slide.id}
                            className={`hc-slide absolute inset-0 overflow-hidden ${cls}`}
                            aria-hidden={!isActive}
                        >
                            <img
                                key={isActive ? imgKey : slide.id}
                                src={slide.imageUrl}
                                alt={slide.alt}
                                className={`hc-img w-full h-full object-cover ${isActive ? `hc-${slide.effect}` : ''}`}
                                style={{ ['--hc-dur' as any]: durCss }}
                                draggable={false}
                                fetchPriority={idx === 0 ? 'high' : 'low'}
                                loading={idx === 0 ? 'eager' : 'lazy'}
                                decoding="async"
                                width={800}
                                height={550}
                            />
                        </div>
                    );
                })}
            </div>

            {/* Overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-transparent opacity-40 pointer-events-none z-10" />

            {/* Badge inferior con label dinámica */}
            <div className="absolute bottom-6 left-6 right-6 z-20 p-5 bg-white/5 backdrop-blur-lg border border-white/10 rounded-2xl">
                <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center shadow-lg flex-shrink-0">
                        <i className="fas fa-print text-white text-xl"></i>
                    </div>
                    <div className="min-w-0">
                        <p className="text-white font-bold truncate">
                            {currentSlide.label || FALLBACK_LABEL}
                        </p>
                        <p className="text-slate-300 text-sm truncate">{FALLBACK_DESC}</p>
                    </div>
                </div>
            </div>

            {/* Puntos de navegación */}
            {slides.length > 1 && (
                <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
                    {slides.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => goTo(i)}
                            className={`rounded-full transition-all duration-300 focus:outline-none ${
                                i === active ? 'w-6 h-2.5 bg-white' : 'w-2.5 h-2.5 bg-white/45 hover:bg-white/75'
                            }`}
                            aria-label={`Ir a imagen ${i + 1}`}
                        />
                    ))}
                </div>
            )}

            {/* Flechas (desktop) */}
            {slides.length > 1 && (
                <>
                    <button
                        onClick={() => goTo((active - 1 + slides.length) % slides.length)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm transition-all"
                        aria-label="Anterior"
                    >
                        <i className="fas fa-chevron-left text-xs"></i>
                    </button>
                    <button
                        onClick={() => goTo((active + 1) % slides.length)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 z-30 w-9 h-9 rounded-full bg-black/35 hover:bg-black/60 text-white flex items-center justify-center backdrop-blur-sm transition-all"
                        aria-label="Siguiente"
                    >
                        <i className="fas fa-chevron-right text-xs"></i>
                    </button>
                </>
            )}
        </div>
        </div>
    );
};

export default HeroCarousel;
